import { Injectable, computed, inject } from '@angular/core';
import { Observable, catchError, delay, map, of, tap, throwError } from 'rxjs';

import { ApiService } from '../../core/services/api.service';
import { ApiConstants } from '../../core/constants/api.constants';
import { AuthConstants } from '../constants/auth.constants';
import { AuthenticationService } from './authentication.service';
import { environment } from '../../../environments/environment';
import dummyUsers from '../../data/dummy-users.json';
import {
  LoginCredentials,
  LoginResponse,
  OtpVerification,
  PasswordResetConfirmation,
  PasswordResetRequest,
  RegisterCredentials
} from '../interfaces/auth.type';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly authenticationService = inject(AuthenticationService);

  readonly isAuthenticated = this.authenticationService.isAuthenticated;
  readonly userName = computed(() => this.authenticationService.currentUser()?.name ?? null);

  getRole(): string {
    return this.authenticationService.role();
  }

  hasRole(allowedRoles: string[]): boolean {
    return this.authenticationService.hasRole(allowedRoles);
  }

  /**
   * Re-derives the in-memory current user from the stored token (called once on app start).
   * No user detail is persisted - a refresh always re-fetches/re-derives it from the token.
   */
  loadCurrentUser(): Observable<void> {
    const token = localStorage.getItem(AuthConstants.TOKEN_KEY);
    if (!token) {
      return of(undefined);
    }

    if (environment.dummy) {
      const match = dummyUsers.find((user) => user.token === token);
      if (match) {
        this.authenticationService.setCurrentUser({
          name: match.name,
          role: match.role,
          email: match.email,
          phone: match.phone
        });
      }
      return of(undefined);
    }

    return this.api.get<LoginResponse['user']>(ApiConstants.AUTH.ME).pipe(
      tap((user) => this.authenticationService.setCurrentUser(user)),
      map(() => undefined),
      catchError(() => {
        this.clearSession();
        return of(undefined);
      })
    );
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    if (environment.dummy) {
      return this.loginWithDummyData(credentials);
    }

    return this.api.post<LoginResponse>(ApiConstants.AUTH.LOGIN, credentials).pipe(tap((response) => this.persistSession(response)));
  }

  register(credentials: RegisterCredentials): Observable<LoginResponse> {
    if (environment.dummy) {
      const response: LoginResponse = {
        token: 'dummy-token-' + Date.now(),
        user: { name: credentials.fullName, role: 'user', email: credentials.email, phone: credentials.phoneNumber }
      };
      return of(response).pipe(
        delay(300),
        tap(() => this.persistSession(response))
      );
    }

    return this.api
      .post<LoginResponse>(ApiConstants.AUTH.REGISTER, credentials)
      .pipe(tap((response) => this.persistSession(response)));
  }

  sendOtp(phoneNumber: string): Observable<void> {
    if (environment.dummy) {
      return of(undefined).pipe(delay(400));
    }

    return this.api.post<void>(ApiConstants.AUTH.SEND_OTP, { phoneNumber });
  }

  verifyOtp(verification: OtpVerification): Observable<LoginResponse> {
    if (environment.dummy) {
      return this.verifyOtpWithDummyData(verification);
    }

    return this.api
      .post<LoginResponse>(ApiConstants.AUTH.VERIFY_OTP, verification)
      .pipe(tap((response) => this.persistSession(response)));
  }

  sendPasswordResetOtp(request: PasswordResetRequest): Observable<void> {
    if (environment.dummy) {
      return of(undefined).pipe(delay(400));
    }

    return this.api.post<void>(ApiConstants.AUTH.FORGOT_PASSWORD_SEND_OTP, request);
  }

  resetPassword(confirmation: PasswordResetConfirmation): Observable<void> {
    if (environment.dummy) {
      return this.resetPasswordWithDummyData(confirmation);
    }

    return this.api.post<void>(ApiConstants.AUTH.FORGOT_PASSWORD_RESET, confirmation);
  }

  logout(): Observable<void> {
    const request = environment.dummy ? of(undefined).pipe(delay(200)) : this.api.post<void>(ApiConstants.AUTH.LOGOUT, {});

    return request.pipe(
      tap(() => this.clearSession()),
      catchError(() => {
        this.clearSession();
        return of(undefined);
      })
    );
  }

  private loginWithDummyData(credentials: LoginCredentials): Observable<LoginResponse> {
    const match = dummyUsers.find((user) => user.email === credentials.email && user.password === credentials.password);

    if (!match) {
      return throwError(() => new Error('Invalid email or password.')).pipe(delay(300));
    }

    const response: LoginResponse = {
      token: match.token,
      user: { name: match.name, role: match.role, email: match.email, phone: match.phone }
    };
    return of(response).pipe(
      delay(300),
      tap(() => this.persistSession(response))
    );
  }

  private verifyOtpWithDummyData(verification: OtpVerification): Observable<LoginResponse> {
    if (verification.code !== '123456') {
      return throwError(() => new Error('Invalid OTP. Please try again.')).pipe(delay(300));
    }

    const match = dummyUsers.find((user) => user.phone === verification.phoneNumber);
    const response: LoginResponse = match
      ? { token: match.token, user: { name: match.name, role: match.role, email: match.email, phone: match.phone } }
      : { token: 'dummy-token-otp-' + Date.now(), user: { name: 'OTP User', role: 'user', phone: verification.phoneNumber } };

    return of(response).pipe(
      delay(300),
      tap(() => this.persistSession(response))
    );
  }

  private resetPasswordWithDummyData(confirmation: PasswordResetConfirmation): Observable<void> {
    if (confirmation.code !== '123456') {
      return throwError(() => new Error('Invalid OTP. Please try again.')).pipe(delay(300));
    }

    const match = dummyUsers.find((user) =>
      confirmation.method === 'phone' ? user.phone === confirmation.phoneNumber : user.email === confirmation.email
    );

    if (!match) {
      return throwError(() => new Error('No account found for the given details.')).pipe(delay(300));
    }

    match.password = confirmation.newPassword;
    return of(undefined).pipe(delay(300));
  }

  private persistSession(response: LoginResponse): void {
    localStorage.setItem(AuthConstants.TOKEN_KEY, response.token);
    this.authenticationService.setCurrentUser({
      name: response.user.name,
      role: response.user.role,
      email: response.user.email,
      phone: response.user.phone
    });
  }

  private clearSession(): void {
    localStorage.removeItem(AuthConstants.TOKEN_KEY);
    this.authenticationService.clearCurrentUser();
  }
}
